import { SUPPORT_URLS } from '@hotjar/webapp/constants/support';
import type { Site } from '@hotjar/site';

const knowledgeBaseUrl = 'https://help.hotjar.com/hc/';

/**
 * Create a help url customized for the current site/user.
 * @param urlSlug The url slug for the help centre section
 * @param currentSite The user's current site
 */
const buildHelpCentreURL = (urlSlug: string, currentSite: Site, currentLang = 'en'): string => {
  const url = new URL(`${knowledgeBaseUrl}${currentLang.toLowerCase()}${urlSlug}`);

  if (currentSite?.id) {
    url.searchParams.append('is_verified', (currentSite.lastData !== null).toString());
  }

  return url.href;
};

interface HelpUrlGetter {
  /**
   * @param currentSite The user's current site.
   */
  (currentSite: Site, currentLang: string): string;
}

/**
 * Help url for knowledgebase
 */
export const getKnowledgeBaseUrl: HelpUrlGetter = (currentSite, currentLang) =>
  buildHelpCentreURL('', currentSite, currentLang);

/**
 * Create new ticket url
 */
export const getCreateTicketUrl: HelpUrlGetter = (currentSite, currentLang) =>
  buildHelpCentreURL(SUPPORT_URLS.createTicket, currentSite, currentLang);

/**
 * Help url for plans and billing
 */
export const getPlansAndBillingHelpUrl: HelpUrlGetter = (currentSite, currentLang) =>
  buildHelpCentreURL(SUPPORT_URLS.plansAndBillingHelp, currentSite, currentLang);

/**
 * Help url for new tickets
 */
export const getNewTicketHelpPageUrl: HelpUrlGetter = (currentSite, currentLang) =>
  buildHelpCentreURL(SUPPORT_URLS.newTicketHelp, currentSite, currentLang);

/**
 * Help url for comparing plans
 */
export const getComparePlansUrl: HelpUrlGetter = (currentSite, currentLang) =>
  buildHelpCentreURL(SUPPORT_URLS.comparePlans, currentSite, currentLang);

/**
 * Url for the new CSM ticket form
 */
export const getCreateCSMTicketFormUrl: HelpUrlGetter = (currentSite, currentLang) =>
  buildHelpCentreURL(SUPPORT_URLS.createCSMTicket, currentSite, currentLang);

/**
 * Url for the new Billing and Payment ticket form
 */
export const getNewBillingPaymentTicketFormUrl: HelpUrlGetter = (currentSite, currentLang) =>
  buildHelpCentreURL(SUPPORT_URLS.createBillingPaymentTicket, currentSite, currentLang);

/**
 * Url for the getting started guide
 */
export const getGettingStartedGuideUrl: HelpUrlGetter = (currentSite, currentLang) =>
  buildHelpCentreURL(SUPPORT_URLS.getStarted, currentSite, currentLang);
